import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import PropTypes from "prop-types";
import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { Chart as ChartJS } from "chart.js";
import { Line } from "react-chartjs-2";
import {
  InsightsChartSkeleton,
  InsightsEmptyState,
  InsightsErrorState,
} from "../../components/Insights/InsightsAsyncState";
import { insightsDesignEnabled } from "../../common/utils/insightsDesignEnabled";
import { insightsChartPlotSurfaceSx, statisticCardContainerSx } from "./constants";
import { bone, stone } from "./design/designTokens";
import { facetChartHasSeriesData } from "./pricingChartsUtils";
import { PRICING_REPORT_FACET_MINI_CHART_HEIGHT_PX } from "./pricingReportPrintDimensions";
import { resolveChartJsInstance } from "./chartJsRefUtils";
import { stitchChartCanvasesToDataUrl } from "./stitchChartCanvasesToDataUrl";

const facetPlotAreaBackgroundPlugin = {
  id: "facetPlotAreaBackground",
  beforeDraw(chart) {
    if (!chart.options?.plugins?.facetPlotAreaBackground) return;
    const { ctx, chartArea } = chart;
    if (!chartArea) return;
    ctx.save();
    ctx.fillStyle = insightsDesignEnabled() ? bone : "#f5f5f5";
    ctx.fillRect(
      chartArea.left,
      chartArea.top,
      chartArea.right - chartArea.left,
      chartArea.bottom - chartArea.top
    );
    ctx.restore();
  },
};

ChartJS.register(facetPlotAreaBackgroundPlugin);

function resolveFacetGridColumns(facetCount, { lgUp, mdUp, smUp }) {
  if (facetCount <= 1) return 1;
  if (lgUp) return Math.min(4, facetCount);
  if (mdUp) return Math.min(3, facetCount);
  if (smUp) return Math.min(2, facetCount);
  return 1;
}

function buildFacetMiniChartOptions({ yScale, showXAxis, showYAxisTitle, onActivate }) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "nearest", intersect: true },
    onClick: onActivate ? () => onActivate() : undefined,
    plugins: {
      legend: { display: false },
      facetPlotAreaBackground: true,
      tooltip: {
        callbacks: {
          title: (items) => items[0]?.label ?? "",
          label: (context) => {
            const value = typeof context.parsed?.y === "number" ? context.parsed.y.toFixed(2) : "—";
            return `$${value}`;
          },
        },
      },
    },
    scales: {
      x: {
        display: showXAxis,
        grid: { display: false },
        ticks: {
          autoSkip: true,
          maxTicksLimit: 6,
          maxRotation: 45,
          minRotation: 0,
          font: { size: 10 },
        },
      },
      y: {
        display: true,
        min: yScale?.min,
        max: yScale?.max,
        grid: {
          color: "rgba(0, 0, 0, 0.06)",
        },
        ticks: {
          maxTicksLimit: 4,
          font: { size: 9 },
          callback: (value) => `$${value}`,
        },
        title: showYAxisTitle
          ? { display: true, text: "CAD", font: { size: 10 } }
          : { display: false },
      },
    },
    elements: {
      point: {
        radius: 3,
        hoverRadius: 4,
      },
      line: {
        borderWidth: 2,
      },
    },
  };
}

function buildFacetMiniChartData(facet, labels) {
  return {
    labels,
    datasets: [
      {
        label: facet.label,
        data: facet.data,
        borderColor: facet.color,
        backgroundColor: facet.color,
        tension: 0.2,
        spanGaps: true,
        fill: false,
      },
    ],
  };
}

function facetPlotBackgroundSx() {
  const plotFill = insightsDesignEnabled() ? bone : "#f5f5f5";
  return {
    backgroundColor: plotFill,
    border: insightsDesignEnabled() ? `1px solid ${stone}` : "1px dashed rgba(0, 0, 0, 0.2)",
  };
}

const FacetLineChartGrid = forwardRef(function FacetLineChartGrid(
  {
    title,
    subtitle,
    facetChart,
    isLoading = false,
    error = null,
    framed = true,
    hasData: hasDataProp,
    emptyMessage = "No chart data available for the current filters.",
    onFacetClick,
  },
  ref
) {
  const theme = useTheme();
  const lgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));
  const smUp = useMediaQuery(theme.breakpoints.up("sm"));
  const gridPlotRef = useRef(null);
  const chartRefs = useRef([]);
  const columnsRef = useRef(1);
  const facetLabelsRef = useRef([]);

  const facets = facetChart?.facets || [];
  const labels = facetChart?.labels || [];
  const yScale = facetChart?.yScale || { min: 0, max: 1 };
  const columns = resolveFacetGridColumns(facets.length, { lgUp, mdUp, smUp });
  columnsRef.current = columns;
  facetLabelsRef.current = facets.map((facet) => facet.label ?? "");
  const rowCount = Math.max(1, Math.ceil(facets.length / columns));

  const hasData =
    typeof hasDataProp === "boolean" ? hasDataProp : facetChartHasSeriesData(facetChart);

  const gridTemplateColumns = useMemo(() => {
    if (columns === 1) return "1fr";
    return `repeat(${columns}, minmax(0, 1fr))`;
  }, [columns]);

  useImperativeHandle(ref, () => ({
    isFacetGridComposite: true,
    getPlotElement: () => gridPlotRef.current,
    getChartInstances: () =>
      chartRefs.current.map((entry) => resolveChartJsInstance(entry)).filter(Boolean),
    getFacetGridColumns: () => columnsRef.current,
    toBase64Image: (_format, _quality) => {
      const canvases = chartRefs.current
        .map((entry) => resolveChartJsInstance(entry)?.canvas)
        .filter(Boolean);
      return stitchChartCanvasesToDataUrl(canvases, {
        columns: columnsRef.current,
        labels: facetLabelsRef.current,
      });
    },
    resize: () => {
      chartRefs.current.forEach((entry) => {
        const chartInstance = resolveChartJsInstance(entry);
        if (chartInstance && typeof chartInstance.resize === "function") {
          chartInstance.resize();
        }
      });
    },
  }));

  return (
    <Box
      sx={
        framed
          ? {
              p: 2,
              minWidth: 0,
              width: "100%",
              maxWidth: "100%",
              overflowX: "hidden",
              ...insightsChartPlotSurfaceSx,
            }
          : { minWidth: 0, width: "100%", maxWidth: "100%" }
      }
    >
      {title ? (
        <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, textTransform: "uppercase" }}>
          {title}
        </Typography>
      ) : null}
      {subtitle ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: title ? 0.5 : 0, mb: 1.5 }}>
          {subtitle}
        </Typography>
      ) : null}

      {error ? (
        <InsightsErrorState
          message={error}
          fallbackMessage="Something went wrong while loading chart data."
        />
      ) : null}
      {isLoading ? <InsightsChartSkeleton sx={{ mt: title || subtitle ? 0 : undefined }} /> : null}
      {!isLoading && !error && !hasData ? <InsightsEmptyState message={emptyMessage} /> : null}
      {!isLoading && !error && hasData ? (
        <Box
          ref={gridPlotRef}
          data-pricing-chart-plot
          data-pricing-facet-grid
          sx={{
            minWidth: 0,
            width: "100%",
            maxWidth: "100%",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns,
              gap: 1.5,
            }}
          >
            {facets.map((facet, index) => {
              const column = index % columns;
              const row = Math.floor(index / columns);
              const showXAxis = row === rowCount - 1;
              const showYAxisTitle = column === 0;
              const facetIsClickable =
                typeof onFacetClick === "function" && Boolean(facet.entityValue);
              const handleFacetClick = () => {
                if (!facetIsClickable) return;
                onFacetClick(facet.entityValue);
              };
              const chartOptions = buildFacetMiniChartOptions({
                yScale,
                showXAxis,
                showYAxisTitle,
                onActivate: facetIsClickable ? handleFacetClick : undefined,
              });
              const chartData = buildFacetMiniChartData(facet, labels);

              return (
                <Box
                  key={facet.entityValue || facet.label || index}
                  sx={{
                    minWidth: 0,
                    p: 1,
                    ...statisticCardContainerSx,
                    boxShadow: "none",
                    ...(facetIsClickable
                      ? {
                          cursor: "pointer",
                          transition: "box-shadow 0.15s ease",
                          "&:hover": {
                            boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.35)",
                          },
                        }
                      : {}),
                  }}
                >
                  <Typography
                    align="center"
                    data-pricing-facet-label
                    onClick={facetIsClickable ? handleFacetClick : undefined}
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      mb: 0.5,
                      lineHeight: 1.3,
                    }}
                  >
                    {facet.label}
                  </Typography>
                  <Box
                    data-pricing-facet-plot
                    sx={{
                      height: PRICING_REPORT_FACET_MINI_CHART_HEIGHT_PX,
                      minWidth: 0,
                      width: "100%",
                      ...facetPlotBackgroundSx(),
                    }}
                  >
                    <Line
                      ref={(instance) => {
                        chartRefs.current[index] = instance;
                      }}
                      data={chartData}
                      options={chartOptions}
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
});

FacetLineChartGrid.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  facetChart: PropTypes.shape({
    labels: PropTypes.arrayOf(PropTypes.string),
    facets: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        color: PropTypes.string,
        entityValue: PropTypes.string,
        data: PropTypes.array,
      })
    ),
    yScale: PropTypes.shape({
      min: PropTypes.number,
      max: PropTypes.number,
    }),
  }),
  isLoading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  framed: PropTypes.bool,
  hasData: PropTypes.bool,
  emptyMessage: PropTypes.string,
  onFacetClick: PropTypes.func,
};

export default FacetLineChartGrid;
export { facetChartHasSeriesData };
